import { STATIC_DEFAULT_BUY_TOKEN, STATIC_DEFAULT_SELL_TOKEN } from '@src/constants/defaultTokens'
import { IToken } from '@src/types/token'
import { isInEmbedMode } from '@src/utils/isInEmbedMode'
import { useConfigStore, useTokensStore } from '@src/zustand_stores'

/**
 * Get a token from restricted chains, preferring stable coins
 * @param chains Array of chain IDs to consider
 * @returns A token from the provided chains
 */
function getTokenFromRestrictedChains(chains: number[]): IToken | null {
    const tokens = useTokensStore.getState().tokens

    if (!tokens || tokens.length === 0) {
        return null
    }

    // Filter tokens to only those from allowed chains
    const filteredTokens = tokens.filter(token => chains.includes(token.chainId))

    if (filteredTokens.length === 0) {
        return null
    }

    // Try to find stable coins first across all allowed chains
    const stableTokens = filteredTokens.filter(token => ['USDC', 'USDT', 'DAI', 'BUSD'].includes(token.symbol))

    if (stableTokens.length > 0) {
        return stableTokens[0]
    }

    // Otherwise return any token from allowed chains

    return filteredTokens[0]
}

/**
 * Dynamic function that returns appropriate default tokens based on context
 * In embed mode with restricted chains, it will return tokens from those chains
 * Otherwise, it will return the static defaults
 */
export function getDefaultTokens(): { sellToken: IToken; buyToken: IToken } {
    // Check if we're in embed mode
    const isEmbed = isInEmbedMode()
    const restrictedChains = useConfigStore.getState().restrictedChains
    const tokens = useTokensStore.getState().tokens

    // Check if we're in embed mode
    if (isEmbed) {
        // If we have restricted chains, get tokens from those chains
        if (restrictedChains && restrictedChains.length > 0) {
            // Try to get tokens from restricted chains
            const sellToken = getTokenFromRestrictedChains(restrictedChains)

            if (sellToken) {
                // For buy token, try to find a different token
                // Filter tokens to restricted chains
                const filteredTokens = tokens.filter(token => restrictedChains.includes(token.chainId))

                // Try to find a token that's different from sell token
                // Prefer a token from a different chain if multiple chains are allowed
                const differentChainTokens = filteredTokens.filter(token => token.chainId !== sellToken.chainId)

                let buyToken = null

                // First try to get a token from a different chain
                if (differentChainTokens.length > 0) {
                    // Prefer stablecoins
                    const stableTokens = differentChainTokens.filter(token =>
                        ['USDC', 'USDT', 'DAI', 'BUSD'].includes(token.symbol)
                    )

                    buyToken = stableTokens.length > 0 ? stableTokens[0] : differentChainTokens[0]
                }
                // If no different chain tokens or only one chain is allowed, try to find a different token on the same chain
                else {
                    const sameChainTokens = filteredTokens.filter(
                        token => token.chainId === sellToken.chainId && token.address !== sellToken.address
                    )

                    if (sameChainTokens.length > 0) {
                        buyToken = sameChainTokens[0]
                    } else {
                        // If we can't find a different token, use the same one
                        buyToken = sellToken
                    }
                }

                // Return the tokens
                const result = {
                    sellToken: sellToken,
                    buyToken: buyToken || sellToken,
                }

                return result
            }

            // Fallback to static defaults if no tokens found
            return {
                sellToken: STATIC_DEFAULT_SELL_TOKEN,
                buyToken: STATIC_DEFAULT_BUY_TOKEN,
            }
        }
    }

    // For non-embed mode or no restricted chains, use static defaults
    return {
        sellToken: STATIC_DEFAULT_SELL_TOKEN,
        buyToken: STATIC_DEFAULT_BUY_TOKEN,
    }
}

// Default order expiration settings
export const DEFAULT_CHALLENGE_OFFSET = 30
export const DEFAULT_CHALLENGE_WINDOW = 50
